import React, { useEffect, useMemo, useState } from 'react';
import { colorToCss, cssToColor, fmtGrade, gradeLegendRows } from '../../Utils/gradePalette';
import { getLithLegendItems } from '../../Utils/lithPalette';
import { useGradePaletteStore } from '../../store/gradePaletteStore';
import { useHoleDisplayStore } from '../../store/holeDisplayStore';
import LegendToggleButton from './LegendToggleButton';

function EyeIcon({ off }) {
  if (off) {
    return (
      <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden>
        <path d="M2 8s2.4-4 6-4 6 4 6 4-2.4 4-6 4-6-4-6-4z" stroke="currentColor" strokeWidth="1.3" />
        <circle cx="8" cy="8" r="1.7" fill="currentColor" />
        <path d="M3 13L13 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden>
      <path d="M2 8s2.4-4 6-4 6 4 6 4-2.4 4-6 4-6-4-6-4z" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="8" cy="8" r="1.7" fill="currentColor" />
    </svg>
  );
}

function BinEyeButton({ hidden, label, onClick }) {
  return (
    <button
      type="button"
      title={hidden ? `Show ${label}` : `Hide ${label}`}
      aria-label={hidden ? `Show ${label}` : `Hide ${label}`}
      aria-pressed={!hidden}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className={`m-0 h-5 w-5 shrink-0 inline-flex items-center justify-center rounded-sm border transition-colors ${
        hidden
          ? 'border-zinc-700 text-zinc-600 hover:text-zinc-400 hover:border-zinc-500'
          : 'border-yellow-600/30 text-yellow-500/80 hover:bg-yellow-500/10 hover:text-yellow-300'
      }`}
    >
      <EyeIcon off={hidden} />
    </button>
  );
}

export function GradeBoundInput({ value, placeholder, ariaLabel, onCommit, step = '0.1' }) {
  const [text, setText] = useState(value == null || value === '' ? '' : String(value));

  useEffect(() => {
    setText(value == null || value === '' ? '' : String(value));
  }, [value]);

  const commit = () => {
    const t = String(text || '').trim();
    if (t === '') {
      onCommit(null);
      return;
    }
    const n = Number(t);
    if (Number.isFinite(n)) {
      onCommit(n);
      setText(String(n));
    } else {
      setText(value == null || value === '' ? '' : String(value));
    }
  };

  return (
    <input
      type="number"
      step={step}
      inputMode="decimal"
      aria-label={ariaLabel}
      placeholder={placeholder}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
        e.stopPropagation();
      }}
      onClick={(e) => e.stopPropagation()}
      className="w-full min-w-0 px-1.5 py-1 text-[11px] tabular-nums border border-yellow-600/25 rounded-sm bg-black/60 text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-yellow-500/50"
    />
  );
}

export function GradeLegendBody({ compact = false }) {
  const thresholds = useGradePaletteStore((s) => s.thresholds);
  const toggleBinHidden = useGradePaletteStore((s) => s.toggleBinHidden);
  const rows = useMemo(() => gradeLegendRows(thresholds), [thresholds]);
  return (
    <ul className={compact ? 'space-y-1' : 'space-y-1.5'}>
      {rows.map((row, index) => (
        <li
          key={row.label}
          className={`flex items-center gap-1.5 text-[10px] text-zinc-300 ${row.hidden ? 'opacity-40' : ''}`}
        >
          <span className="h-2.5 w-2.5 rounded-sm border border-white/20 shrink-0" style={{ background: row.css }} />
          <span className={`min-w-0 flex-1 truncate ${row.hidden ? 'line-through' : ''}`}>{row.label}</span>
          <BinEyeButton hidden={row.hidden} label={row.label} onClick={() => toggleBinHidden(index)} />
        </li>
      ))}
    </ul>
  );
}

export function LithLegendBody({ codes, hideable = true }) {
  const hiddenLithCodes = useHoleDisplayStore((s) => s.hiddenLithCodes);
  const toggleLithCodeHidden = useHoleDisplayStore((s) => s.toggleLithCodeHidden);
  const items = useMemo(() => getLithLegendItems(codes), [codes]);
  if (!items.length) {
    return <p className="text-[10px] text-zinc-500">Turn a geology layer on to list lithology codes.</p>;
  }
  return (
    <ul className="space-y-1 max-h-48 overflow-y-auto pr-0.5">
      {items.map((item) => {
        const hidden = hideable && hiddenLithCodes.includes(item.code);
        return (
          <li
            key={item.code}
            className={`flex items-center gap-1.5 text-[10px] text-zinc-300 ${hidden ? 'opacity-40' : ''}`}
          >
            <span className="h-2.5 w-2.5 rounded-sm border border-white/20 shrink-0" style={{ background: item.color }} />
            <span className={`min-w-0 flex-1 truncate ${hidden ? 'line-through' : ''}`}>{item.code}</span>
            {hideable ? (
              <BinEyeButton hidden={hidden} label={item.code} onClick={() => toggleLithCodeHidden(item.code)} />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export default function EditableGradeLegend({
  title = 'Grade legend',
  overlayOpen,
  onToggleOverlay,
  children,
}) {
  const thresholds = useGradePaletteStore((s) => s.thresholds);
  const setBinMin = useGradePaletteStore((s) => s.setBinMin);
  const setBinColor = useGradePaletteStore((s) => s.setBinColor);
  const addBin = useGradePaletteStore((s) => s.addBin);
  const removeBin = useGradePaletteStore((s) => s.removeBin);
  const toggleBinHidden = useGradePaletteStore((s) => s.toggleBinHidden);
  const resetPalette = useGradePaletteStore((s) => s.resetPalette);
  const rows = useMemo(() => gradeLegendRows(thresholds), [thresholds]);
  const canRemove = rows.filter((row) => row.finite).length > 1;

  return (
    <div className="rounded-sm border border-yellow-600/20 bg-black/30 px-1.5 py-1.5 space-y-1.5">
      <div className="flex items-center gap-1">
        <p className="text-[9px] uppercase tracking-wider text-zinc-500 flex-1">{title}</p>
        {typeof onToggleOverlay === 'function' && (
          <LegendToggleButton pressed={overlayOpen} onClick={onToggleOverlay} />
        )}
      </div>
      {children}
      <ul className="space-y-1">
        {rows.map((row, index) => (
          <li
            key={`${row.finite ? row.min : 'floor'}-${index}`}
            className={`flex items-center gap-1 ${row.hidden ? 'opacity-45' : ''}`}
          >
            <BinEyeButton hidden={row.hidden} label={row.label} onClick={() => toggleBinHidden(index)} />
            <input
              type="color"
              value={colorToCss(row.color)}
              aria-label={`${row.label} colour`}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setBinColor(index, cssToColor(e.target.value))}
              className="h-5 w-5 shrink-0 cursor-pointer rounded-sm border border-yellow-600/30 bg-transparent p-0"
            />
            {row.finite ? (
              <div className="flex min-w-0 flex-1 items-center gap-1">
                <span className="shrink-0 text-[10px] text-zinc-500">≥</span>
                <GradeBoundInput
                  value={row.min}
                  placeholder={fmtGrade(row.min)}
                  ariaLabel={`${row.label} threshold`}
                  onCommit={(n) => {
                    if (Number.isFinite(n)) setBinMin(index, n);
                  }}
                />
                <span className="shrink-0 text-[10px] text-zinc-500">g/t</span>
              </div>
            ) : (
              <span className={`flex-1 truncate text-[11px] text-zinc-300 ${row.hidden ? 'line-through' : ''}`}>
                {row.label}
              </span>
            )}
            {row.finite && canRemove && (
              <button
                type="button"
                title="Remove bin"
                onClick={() => removeBin(index)}
                className="m-0 h-5 w-5 shrink-0 text-[10px] text-zinc-500 hover:text-red-400"
              >
                ✕
              </button>
            )}
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={addBin}
          className="m-0 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded-sm border border-yellow-600/40 text-yellow-400 hover:bg-yellow-500/10"
        >
          Add bin
        </button>
        <button
          type="button"
          onClick={resetPalette}
          className="m-0 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-zinc-500 hover:text-yellow-400"
        >
          Reset colours
        </button>
      </div>
    </div>
  );
}
